<template>
  <InputLabelsWrapper>
    <!-- Pass label slots to InputLabelsWrapper slots of the same name -->
    <template v-for="name in ['label', 'helperText']" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps" />
    </template>

    <div class="space-y-3">
      <div
        v-for="(slot, index) in slots"
        :key="index"
        class="flex items-center gap-3"
      >
        <div class="flex w-full flex-wrap gap-3 *:flex-1">
          <!-- Slot start -->
          <InputField v-model="slot.from" type="time" step="3600" hide-clear>
            <template #addonLeft>Van</template>
          </InputField>
          <!-- Slot end -->
          <InputField
            v-model="slot.to"
            type="time"
            step="3600"
            hide-clear
            :state-error="slot.error"
          >
            <template #addonLeft>Tot</template>
          </InputField>
        </div>
        <!-- Remove slot -->
        <BaseButton plain square @click="removeSlot(index)">
          <BaseIcon name="delete-bin" class="text-danger! text-xl" />
        </BaseButton>
      </div>

      <BaseButton plain @click="addSlot"> Tijdvak toevoegen </BaseButton>
    </div>
  </InputLabelsWrapper>
</template>

<script setup>
import { ref, watch } from "vue"
import {
  BaseButton,
  BaseIcon,
  InputLabelsWrapper,
  InputField,
} from "@repowerednl/ui-component-library"

// Array of integers representing hours
const modelValue = defineModel({ type: Array, default: () => [] })

// Collection of slots shown in the UI
const slots = ref([])

/**
 * Sync inward: external hour changes → rebuild slots.
 *
 * Prevent unnecessary updates if the slots already match the external hours.
 */
watch(
  modelValue,
  (hours) => {
    if (arraysEqual(slotsToHours(slots.value), hours)) {
      return
    }
    slots.value =
      hours.length > 0 ? hoursToSlots(hours) : [{ from: "09:00", to: "17:00" }]
  },
  { immediate: true },
)

/**
 * Sync outward: slot edits → emit hours.
 *
 * Prevent unnecessary updates if the emitted hours already match the external
 * model.
 */
watch(
  slots,
  (slotList) => {
    const hours = slotsToHours(slotList)
    if (!arraysEqual(hours, modelValue.value)) {
      modelValue.value = hours
    }
  },
  { deep: true },
)

function addSlot() {
  slots.value.push({ from: "09:00", to: "17:00" })
}

function removeSlot(index) {
  slots.value.splice(index, 1)
}

/**
 * Converts collection of start to end times into sorted list of individual
 * hours. An end time before or on the start time yield an empty set.
 */
function slotsToHours(slotList) {
  const hours = new Set()
  for (const slot of slotList) {
    if (slot.from && slot.to) {
      const from = parseHour(slot.from)
      // Consider end time of 0:00 as 24:00 (end of the day)
      const to = parseHour(slot.to) || 24
      for (let hour = from; hour < to; hour++) {
        hours.add(hour)
      }
    }
  }
  return [...hours].sort((a, b) => a - b)
}

/**
 * Converts a sorted list of individual hours into a collection of start to end
 * times. Consecutive hours are merged into a single slot.
 */
function hoursToSlots(hours) {
  if (hours.length === 0) {
    return []
  }
  const sorted = [...hours].sort((a, b) => a - b)
  const result = []
  let [start] = sorted
  let [end] = sorted
  for (let i = 1; i <= sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i]
    } else {
      result.push({ from: formatHour(start), to: formatHour(end + 1) })
      start = sorted[i]
      end = sorted[i]
    }
  }
  return result
}

/**
 * Extracts the hour component from a time string in the format "HH:MM".
 */
function parseHour(time) {
  return Number(time.split(":")[0])
}

/**
 * Formats an hour integer as a time string in the format "HH:00".
 */
function formatHour(h) {
  return `${String(h % 24).padStart(2, "0")}:00`
}

/**
 * Checks if two arrays of numbers are equal in membership (but not necessarily
 * in order).
 */
function arraysEqual(a, b) {
  return a.length === b.length && a.every((v, i) => v === b[i])
}

/**
 * Returns false if any slot has an end time before or equal to the start time.
 * Also gives the invalid slots an error state.
 *
 * This does not get called automatically, it must be invoked manually. Invalid
 * slots will yield empty sets, the model value will still be valid.
 */
function validate() {
  let valid = true
  for (const slot of slots.value) {
    if (slot.from && slot.to) {
      const from = parseHour(slot.from)
      const to = parseHour(slot.to) || 24
      if (to <= from) {
        slot.error = true
        valid = false
      } else {
        slot.error = false
      }
    }
  }
  return valid
}

defineExpose({
  validate,
})
</script>
